import React, { useState, useEffect } from "react";
import axios from "axios";
import { 
  CheckSquare, Clock, AlertTriangle, Calendar, Filter, Plus, Pencil, ArrowRight, Flame
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import Layout from "../components/Layout";
import { API, useProject } from "../App";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";
import { usePermissions } from "../hooks/usePermissions";
import { useUrlFilters } from "../hooks/useUrlFilters";
import { useScrollRestore } from "../hooks/useScrollRestore";
import SavedViewsBar from "../components/SavedViewsBar";

const TasksPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { canEdit } = usePermissions();
  const { selectedProjectId } = useProject();
  const [tasks, setTasks] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, overdue: 0, completed: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter, resetFilter, activeFilterCount] = useUrlFilters({ status: "", priority: "" });
  useScrollRestore([tasks.length]);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  
  // Task completion dialog
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [completingTask, setCompletingTask] = useState(null);
  const [completeForm, setCompleteForm] = useState({ outcome: "", outcome_notes: "" });
  const [showFollowUpForm, setShowFollowUpForm] = useState(false);
  const [followUpForm, setFollowUpForm] = useState({ task_type: "call", priority: "medium", due_date: "", description: "" });

  useEffect(() => {
    fetchTasks();
  }, [filter, selectedProjectId]);

  const fetchTasks = async () => {
    try {
      const params = new URLSearchParams();
      if (filter.status) params.append('status', filter.status);
      if (filter.priority) params.append('priority', filter.priority);
      if (selectedProjectId !== "all") params.append('project_id', selectedProjectId);
      
      const response = await axios.get(`${API}/tasks?${params.toString()}`);
      setTasks(response.data.tasks);
      setCounts(response.data.counts);
    } catch (error) {
      console.error("Fetch tasks error:", error);
      toast.error("Failed to fetch tasks");
    } finally {
      setLoading(false);
    }
  };

  const openCompleteDialog = (task) => {
    setCompletingTask(task);
    setCompleteForm({ outcome: "", outcome_notes: "" });
    setShowFollowUpForm(false);
    // Default follow-up due date to tomorrow
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setFollowUpForm({ task_type: "call", priority: "medium", due_date: tomorrow.toISOString().split("T")[0], description: "" });
    setCompleteDialogOpen(true);
  };

  const handleCompleteTask = async () => {
    if (!completingTask) return;
    if (!completeForm.outcome) {
      toast.error("Please select an outcome");
      return;
    }
    try {
      await axios.put(`${API}/tasks/${completingTask.task_id}/complete`, {
        outcome: completeForm.outcome,
        outcome_notes: completeForm.outcome_notes,
      });

      // Create follow-up task if requested
      if (showFollowUpForm && followUpForm.due_date) {
        const autoTitle = `${followUpForm.task_type.replace(/_/g, " ")} - ${completingTask.lead_name || "Lead"}`;
        await axios.post(`${API}/tasks`, {
          lead_id: completingTask.lead_id,
          title: autoTitle,
          description: followUpForm.description || `Follow-up from: ${completingTask.title}`,
          task_type: followUpForm.task_type,
          due_date: new Date(followUpForm.due_date).toISOString(),
          priority: followUpForm.priority,
        });
        toast.success("Task completed & follow-up created");
      } else {
        toast.success("Task completed");
      }

      setCompleteDialogOpen(false);
      setCompletingTask(null);
      fetchTasks();
    } catch (error) {
      toast.error("Failed to complete task");
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await axios.delete(`${API}/tasks/${taskId}`);
      toast.success("Task deleted");
      fetchTasks();
    } catch (error) {
      toast.error("Failed to delete task");
    }
  };

  const saveEditTask = async () => {
    if (!editForm) return;
    try {
      await axios.put(`${API}/tasks/${editForm.task_id}`, {
        title: editForm.title, description: editForm.description,
        task_type: editForm.task_type, due_date: editForm.due_date,
        priority: editForm.priority, assigned_to: editForm.assigned_to,
      });
      toast.success("Task updated");
      setEditOpen(false);
      fetchTasks();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update task"); }
  };

  const pendingTasks = tasks.filter(t => t.status === 'pending');
  const overdueTasks = tasks.filter(t => t.status === 'overdue');
  const completedTasks = tasks.filter(t => t.status === 'completed');

  // Use server-provided counts for accurate stats
  const pendingCount = counts.pending;
  const overdueCount = counts.overdue;
  const completedCount = counts.completed;
  const totalCount = counts.total;

  const getTaskTypeIcon = (type) => {
    switch(type) {
      case 'call': return '📞';
      case 'whatsapp': return '💬';
      case 'site_visit': return '🏠';
      case 'meeting': return '🤝';
      default: return '📋';
    }
  };

  return (
    <Layout>
      <div className="p-4 md:p-6 lg:p-8 space-y-6" data-testid="tasks-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Manrope']">Tasks</h1>
            <p className="text-slate-500 mt-1">{totalCount} total tasks</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Pending</p>
                <p className="text-2xl font-bold text-slate-900 font-['Manrope']">{pendingCount}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Overdue</p>
                <p className="text-2xl font-bold text-red-600 font-['Manrope']">{overdueCount}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <CheckSquare className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Completed</p>
                <p className="text-2xl font-bold text-green-600 font-['Manrope']">{completedCount}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="bg-white border-slate-200">
          <CardContent className="p-4 flex gap-4">
            <Select value={filter.status || "all"} onValueChange={(v) => setFilter({...filter, status: v === "all" ? "" : v})}>
              <SelectTrigger className="w-40" data-testid="filter-status">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filter.priority || "all"} onValueChange={(v) => setFilter({...filter, priority: v === "all" ? "" : v})}>
              <SelectTrigger className="w-40" data-testid="filter-priority">
                <SelectValue placeholder="All Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priority</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex-1" />
            <SavedViewsBar
              path="/tasks"
              currentQuery={searchParams.toString()}
              activeFilterCount={activeFilterCount}
            />
          </CardContent>
        </Card>

        {/* Task List */}
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-16 bg-slate-100 rounded animate-pulse"></div>
                ))}
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {tasks.map((task) => (
                  <div 
                    key={task.task_id} 
                    className={`p-4 flex items-center gap-4 hover:bg-slate-50 transition-colors ${task.status === 'overdue' ? 'bg-red-50' : ''}`}
                    data-testid={`task-${task.task_id}`}
                  >
                    <Checkbox
                      checked={task.status === 'completed'}
                      onCheckedChange={() => task.status !== 'completed' && openCompleteDialog(task)}
                      disabled={task.status === 'completed'}
                      data-testid={`task-checkbox-${task.task_id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{getTaskTypeIcon(task.task_type)}</span>
                        <p className={`font-medium ${task.status === 'completed' ? 'line-through text-slate-400' : 'text-slate-900'}`}>
                          {task.title}
                        </p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          task.priority === 'high' ? 'bg-red-100 text-red-700' : 
                          task.priority === 'medium' ? 'bg-amber-100 text-amber-700' : 
                          'bg-slate-100 text-slate-600'
                        }`}>
                          {task.priority}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          Due: {new Date(task.due_date).toLocaleDateString()}
                        </span>
                        {task.outcome && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">
                            {task.outcome.replace(/_/g, " ")}
                          </span>
                        )}
                        <button 
                          className="text-amber-600 hover:underline"
                          onClick={() => navigate(`/leads/${task.lead_id}`)}
                        >
                          View Lead
                        </button>
                        {task.score_value != null && (
                          <span 
                            className={`inline-flex items-center gap-1 text-xs font-mono px-2 py-0.5 rounded-full ${
                              task.score_value >= 70 ? 'bg-emerald-100 text-emerald-700' :
                              task.score_value >= 40 ? 'bg-amber-100 text-amber-700' :
                              'bg-slate-100 text-slate-600'
                            }`}
                            data-testid={`task-score-${task.task_id}`}
                            title={`Lead Score: ${task.score_value} (${task.lead_score_label || ''})`}
                          >
                            <Flame className="w-3 h-3" />
                            {task.score_value}
                          </span>
                        )}
                      </div>
                      {task.outcome_notes && (
                        <p className="text-xs text-slate-400 mt-1 italic">{task.outcome_notes}</p>
                      )}
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      task.status === 'completed' ? 'bg-green-100 text-green-700' :
                      task.status === 'overdue' ? 'bg-red-100 text-red-700' :
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {task.status}
                    </span>
                    {canEdit("edit_tasks") && task.status !== "completed" && (
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 ml-1" onClick={() => { setEditForm({...task}); setEditOpen(true); }} data-testid={`edit-task-${task.task_id}`}>
                        <Pencil className="w-3.5 h-3.5 text-slate-400" />
                      </Button>
                    )}
                  </div>
                ))}
                {tasks.length === 0 && (
                  <div className="p-12 text-center text-slate-500">
                    <CheckSquare className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>No tasks found</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      {/* Edit Task Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-md" data-testid="edit-task-dialog">
          <DialogHeader><DialogTitle>Edit Task</DialogTitle></DialogHeader>
          {editForm && (
            <div className="space-y-3 py-2">
              <div><label className="text-sm font-medium mb-1 block">Title</label><Input value={editForm.title || ""} onChange={e => setEditForm({...editForm, title: e.target.value})} /></div>
              <div><label className="text-sm font-medium mb-1 block">Description</label><Textarea value={editForm.description || ""} onChange={e => setEditForm({...editForm, description: e.target.value})} rows={2} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-sm font-medium mb-1 block">Type</label>
                  <Select value={editForm.task_type || "call"} onValueChange={v => setEditForm({...editForm, task_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="call">Call</SelectItem><SelectItem value="whatsapp">WhatsApp</SelectItem><SelectItem value="face_to_face">Face to Face Meeting</SelectItem><SelectItem value="email">Email</SelectItem><SelectItem value="document">Document</SelectItem></SelectContent>
                  </Select></div>
                <div><label className="text-sm font-medium mb-1 block">Priority</label>
                  <Select value={editForm.priority || "medium"} onValueChange={v => setEditForm({...editForm, priority: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="high">High</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="low">Low</SelectItem></SelectContent>
                  </Select></div>
              </div>
              <div><label className="text-sm font-medium mb-1 block">Due Date</label><Input type="datetime-local" value={editForm.due_date ? editForm.due_date.slice(0, 16) : ""} onChange={e => setEditForm({...editForm, due_date: e.target.value})} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={saveEditTask} data-testid="save-edit-task-btn">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task Completion Dialog */}
      <Dialog open={completeDialogOpen} onOpenChange={setCompleteDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="complete-task-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Complete Task</DialogTitle>
          </DialogHeader>
          {completingTask && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="font-medium text-slate-900">{completingTask.title}</p>
                <p className="text-sm text-slate-500 mt-0.5">{completingTask.task_type?.replace(/_/g, " ")} &middot; {completingTask.lead_name || "Lead"}</p>
              </div>

              <div>
                <Label>Outcome *</Label>
                <Select value={completeForm.outcome} onValueChange={(v) => setCompleteForm({ ...completeForm, outcome: v })}>
                  <SelectTrigger data-testid="task-outcome-select"><SelectValue placeholder="What was the result?" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="connected">Connected / Successful</SelectItem>
                    <SelectItem value="not_reachable">Not Reachable</SelectItem>
                    <SelectItem value="busy">Busy / Call Back Later</SelectItem>
                    <SelectItem value="interested">Interested — Needs Follow-up</SelectItem>
                    <SelectItem value="not_interested">Not Interested</SelectItem>
                    <SelectItem value="meeting_done">Meeting Done</SelectItem>
                    <SelectItem value="document_collected">Document Collected/Sent</SelectItem>
                    <SelectItem value="rescheduled">Rescheduled</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Notes</Label>
                <Textarea
                  placeholder="Brief notes about what happened..."
                  value={completeForm.outcome_notes}
                  onChange={(e) => setCompleteForm({ ...completeForm, outcome_notes: e.target.value })}
                  rows={3}
                  data-testid="task-outcome-notes"
                />
              </div>

              <div className="border-t pt-4">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Create follow-up task?</Label>
                  <Button
                    variant={showFollowUpForm ? "default" : "outline"}
                    size="sm"
                    onClick={() => setShowFollowUpForm(!showFollowUpForm)}
                    data-testid="toggle-followup-btn"
                  >
                    {showFollowUpForm ? "Remove" : "Yes, add follow-up"}
                  </Button>
                </div>

                {showFollowUpForm && (
                  <div className="mt-3 space-y-3 bg-slate-50 rounded-lg p-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Type</Label>
                        <Select value={followUpForm.task_type} onValueChange={(v) => setFollowUpForm({ ...followUpForm, task_type: v })}>
                          <SelectTrigger data-testid="followup-type-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="call">Call</SelectItem>
                            <SelectItem value="whatsapp">WhatsApp</SelectItem>
                            <SelectItem value="face_to_face">Face to Face Meeting</SelectItem>
                            <SelectItem value="email">Email</SelectItem>
                            <SelectItem value="document">Document</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Priority</Label>
                        <Select value={followUpForm.priority} onValueChange={(v) => setFollowUpForm({ ...followUpForm, priority: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="high">High</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="low">Low</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Due Date</Label>
                      <Input
                        type="date"
                        value={followUpForm.due_date}
                        onChange={(e) => setFollowUpForm({ ...followUpForm, due_date: e.target.value })}
                        data-testid="followup-due-date"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Description</Label>
                      <Textarea
                        placeholder="Follow-up task details..."
                        value={followUpForm.description}
                        onChange={(e) => setFollowUpForm({ ...followUpForm, description: e.target.value })}
                        rows={2}
                      />
                    </div>
                  </div>
                )}
              </div>

              <Button onClick={handleCompleteTask} className="w-full" data-testid="confirm-complete-task-btn">
                <CheckSquare className="w-4 h-4 mr-2" />
                {showFollowUpForm ? "Complete & Create Follow-up" : "Mark as Complete"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default TasksPage;
